import React from 'react';
import { motion } from 'framer-motion';
import { 
  GraduationCap, 
  Globe, 
  Users, 
  BookOpen, 
  FileText, 
  Languages, 
  Briefcase,
  Heart,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

export const ServicesPage = () => {
  const services = [
    {
      icon: <GraduationCap size={48} />,
      title: 'College Admission Guidance',
      description: 'Expert assistance with applications to public and private colleges across Canada',
      features: [
        'College selection based on your profile',
        'Application review and optimization',
        'Document preparation support',
        'Admission requirements guidance',
        'Timeline management'
      ]
    },
    {
      icon: <Globe size={48} />,
      title: 'International Student Counseling',
      description: 'Comprehensive education support for international students',
      features: [
        'College selection and application guidance',
        'Pre-arrival orientation and preparation',
        'Accommodation assistance',
        'Cultural adaptation support',
        'Post-arrival settlement services'
      ]
    },
    {
      icon: <Users size={48} />,
      title: 'Domestic Student Support',
      description: 'Personalized education pathways for Canadian residents',
      features: [
        'Program selection counseling',
        'Career pathway planning',
        'Financial aid information',
        'Transfer credit assessment',
        'Academic planning'
      ]
    },
    {
      icon: <Briefcase size={48} />,
      title: 'Course & Career Guidance',
      description: 'Strategic planning aligned with your career goals',
      features: [
        'Career assessment and counseling',
        'Industry trend analysis',
        'Program-career alignment',
        'Skill development planning',
        'Job market insights'
      ]
    },
    {
      icon: <FileText size={48} />,
      title: 'Application Assistance',
      description: 'End-to-end support for all application processes',
      features: [
        'Document collection and verification',
        'Statement of Purpose writing support',
        'Letter of Recommendation guidance',
        'Application form completion',
        'Follow-up with institutions'
      ]
    },
    {
      icon: <Languages size={48} />,
      title: 'Language Program Guidance',
      description: 'Support for English and French language program selection',
      features: [
        'Language proficiency assessment',
        'ESL/FSL program recommendations',
        'Test preparation guidance (IELTS, TOEFL)',
        'Language school partnerships',
        'Pathway program options'
      ]
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Initial Consultation',
      description: 'Free consultation to understand your goals, background, and preferences'
    },
    {
      step: '02',
      title: 'Profile Assessment',
      description: 'Comprehensive evaluation of your academic profile and career aspirations'
    },
    {
      step: '03',
      title: 'College & Program Selection',
      description: 'Personalized recommendations based on your profile and goals'
    },
    {
      step: '04',
      title: 'Application Preparation',
      description: 'Guidance through document preparation and application submission'
    },
    {
      step: '05',
      title: 'Admission Support',
      description: 'Follow-up with institutions and support until you receive your offer letter'
    },
    {
      step: '06',
      title: 'Post-Admission Assistance',
      description: 'Continued support with enrollment, visa, and pre-departure preparation'
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      {/* Hero */}
      <section className="py-20 md:py-32 bg-primary text-white" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p className="text-secondary text-sm tracking-widest uppercase font-semibold mb-4">Our Services</p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-tight mb-6" data-testid="services-title">
              Comprehensive Education Solutions
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-slate-300">
              From college selection to admission and beyond, we provide end-to-end support 
              for your educational journey in Canada.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-32 bg-white" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative overflow-hidden bg-white p-8 border border-slate-100 hover:border-secondary/50 transition-all duration-500"
                data-testid={`service-detail-card-${index}`}
              >
                <div className="text-secondary mb-4">{service.icon}</div>
                <h3 className="text-2xl font-semibold mb-3">{service.title}</h3>
                <p className="text-slate-600 leading-relaxed mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle className="text-secondary mt-0.5 flex-shrink-0" size={16} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="absolute bottom-0 left-0 w-full h-1 bg-secondary transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 md:py-32 bg-background-alt" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-secondary text-sm tracking-widest uppercase font-semibold mb-4">How We Work</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="process-title">
              Your Journey With ELARA
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Our structured approach ensures you receive personalized support at every step
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative"
                data-testid={`process-step-${index}`}
              >
                <div className="text-6xl font-bold text-secondary/20 mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 md:py-32 bg-white" data-testid="included-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1758518731468-98e90ffd7430?auto=format&fit=crop&q=80"
                alt="Student Consulting"
                className="w-full h-[500px] object-cover"
                data-testid="included-image"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <p className="text-secondary text-sm tracking-widest uppercase font-semibold mb-4">What You Get</p>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6" data-testid="included-title">
                Complete Support Package
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                When you choose ELARA Education, you get more than just guidance—you get a dedicated partner 
                committed to your success.
              </p>
              <ul className="space-y-4">
                {[
                  'One-on-one consultation with experienced counselors',
                  'Personalized college and program recommendations',
                  'Application review and feedback',
                  'Document preparation assistance',
                  'Ongoing support throughout the admission process',
                  'Post-admission guidance and resources',
                  'Access to our network of partner colleges',
                  'Regular updates and communication'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Heart className="text-secondary mt-1 flex-shrink-0" size={20} />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-white" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6" data-testid="services-cta-title">
              Let's Get Started
            </h2>
            <p className="text-slate-300 text-lg leading-relaxed mb-12">
              Book a free consultation to discuss your education goals and how we can help you achieve them.
            </p>
            <Link
              to="/contact"
              data-testid="services-cta-button"
              className="inline-flex items-center gap-2 px-8 py-4 bg-secondary text-primary font-semibold tracking-wide hover:bg-secondary-light transition-all duration-300 group"
            >
              Schedule Consultation
              <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};